/**
 * Vosk 模型懒加载管理器
 *
 * 功能：
 * - 延迟加载：仅在真正需要时才下载 43MB 的模型文件
 * - 单例模式：确保模型只被加载一次
 * - 状态管理：提供加载状态、进度和错误信息
 * - CDN 支持：支持从环境变量配置的 CDN 加载模型
 */

import { createModel, Model } from 'vosk-browser';
import logger from '@/utils/logger';

type ModelStatus = 'idle' | 'loading' | 'loaded' | 'error';

interface LoadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

class VoskModelManager {
  private static instance: VoskModelManager;

  private model: Model | null = null;

  private status: ModelStatus = 'idle';

  private error: string | null = null;

  private loadingPromise: Promise<Model> | null = null;

  private progressListeners: Array<(progress: LoadProgress) => void> = [];

  // 从环境变量获取模型 URL，支持 CDN 部署
  private readonly modelUrl: string;

  private constructor() {
    // 优先使用环境变量配置的 CDN URL
    // 如果没有配置，则使用本地 public 目录的模型文件
    this.modelUrl =
      process.env.REACT_APP_VOSK_MODEL_URL || '/vosk-model-small-cn-0.22.tar.gz';

    logger.info('[VoskModelManager] 初始化，模型 URL:', this.modelUrl);
  }

  /**
   * 获取单例实例
   */
  public static getInstance(): VoskModelManager {
    if (!VoskModelManager.instance) {
      VoskModelManager.instance = new VoskModelManager();
    }
    return VoskModelManager.instance;
  }

  /**
   * 懒加载模型
   * 如果模型已加载，直接返回；否则开始下载
   */
  public async loadModel(): Promise<Model> {
    // 如果已经加载完成，直接返回
    if (this.model && this.status === 'loaded') {
      logger.info('[VoskModelManager] 模型已加载，直接返回');
      return this.model;
    }

    // 如果正在加载中，返回现有的加载 Promise
    if (this.loadingPromise) {
      logger.info('[VoskModelManager] 模型加载中，等待完成...');
      return this.loadingPromise;
    }

    // 开始新的加载流程
    this.status = 'loading';
    this.error = null;

    logger.info('[VoskModelManager] 开始加载模型:', this.modelUrl);

    this.loadingPromise = this.performLoad();

    try {
      const model = await this.loadingPromise;
      this.model = model;
      this.status = 'loaded';
      logger.info('[VoskModelManager] 模型加载成功');
      return model;
    } catch (error) {
      this.status = 'error';
      this.error = String(error);
      logger.error('[VoskModelManager] 模型加载失败:', error);
      throw error;
    } finally {
      this.loadingPromise = null;
    }
  }

  /**
   * 执行实际的模型加载
   */
  private async performLoad(): Promise<Model> {
    try {
      // 使用 createModel 加载模型
      // 第二个参数 -2 表示日志级别为 Error only（减少控制台输出）
      const model = await createModel(this.modelUrl, -2);

      // 通知进度监听器：加载完成
      this.notifyProgress({ loaded: 100, total: 100, percentage: 100 });

      return model;
    } catch (error) {
      logger.error('[VoskModelManager] createModel 失败:', error);
      throw new Error(`Vosk 模型加载失败: ${error}`);
    }
  }

  /**
   * 添加进度监听器
   */
  public onProgress(listener: (progress: LoadProgress) => void): () => void {
    this.progressListeners.push(listener);

    // 返回取消监听的函数
    return () => {
      const index = this.progressListeners.indexOf(listener);
      if (index > -1) {
        this.progressListeners.splice(index, 1);
      }
    };
  }

  /**
   * 通知所有进度监听器
   */
  private notifyProgress(progress: LoadProgress): void {
    this.progressListeners.forEach((listener) => {
      try {
        listener(progress);
      } catch (error) {
        logger.error('[VoskModelManager] 进度监听器执行失败:', error);
      }
    });
  }

  /**
   * 获取当前状态
   */
  public getStatus(): ModelStatus {
    return this.status;
  }

  /**
   * 获取错误信息
   */
  public getError(): string | null {
    return this.error;
  }

  /**
   * 获取已加载的模型（如果有）
   */
  public getModel(): Model | null {
    return this.model;
  }

  /**
   * 判断模型是否已加载
   */
  public isLoaded(): boolean {
    return this.status === 'loaded' && this.model !== null;
  }

  /**
   * 清理资源（用于卸载）
   */
  public async cleanup(): Promise<void> {
    if (this.model) {
      try {
        await this.model.terminate();
        logger.info('[VoskModelManager] 模型资源已清理');
      } catch (error) {
        logger.error('[VoskModelManager] 模型清理失败:', error);
      }
      this.model = null;
    }

    this.status = 'idle';
    this.error = null;
    this.loadingPromise = null;
    this.progressListeners = [];
  }
}

// 导出单例实例
export const voskModelManager = VoskModelManager.getInstance();

// 导出类型
export type { ModelStatus, LoadProgress };
